/*
 * Getters for the [energy_management_system_groups store]{link module:store/energy_management_system_groups}
 *
 * @module store/energy_management_system_groups/getters
 */

import { sortSelectOptions } from '@/components/helpers'
/*
 * Getter to get Information for a single EmsGroup
 *
 * @function
 *
 * @return {function}
 */
export const getEmsGroup = (state) => {
  return (id) => {
    return state.emsGroups.hash[id]
  }
}

/*
 * Getter to get all EmsGroups sorted ordered under the top EmsGroup in each tree
 *
 * Function to create the data/structure for the energy_management_system_groups/index view Array{object}
 * TODO: why is this a function, although it does NOT have any argument. Cann't we change it to a 'plain' getter?
 *
 * @function
 *
 * @return {function}
 */
export const getEmsGroupsUnderOrga = (state) => {
  return () => {
    const list = []

    for (const orgaGroupId in state.emsGroups.hierarchy) {
      list.push({
        id: state.emsGroups.hash[orgaGroupId].id,
        name: state.emsGroups.hash[orgaGroupId].name,
        children: [
          {
            id: state.emsGroups.hash[orgaGroupId].id,
            name: state.emsGroups.hash[orgaGroupId].name
          }
        ].concat(gatherEmsGroups(state, state.emsGroups.hierarchy[orgaGroupId]))
      })
    }

    return list
  }
}

/*
 * Getter to get only the top EmsGroup in each tree
 *
 * Function to create the data/structure for the energy_management_system_groups/index view Array{object}
 *
 * @function
 *
 * @return {function}
 */
export const getOnlyOrga = (state) => {
  return () => {
    const list = []

    for (const orgaGroupId in state.emsGroups.hierarchy) {
      list.push({
        id: state.emsGroups.hash[orgaGroupId].id,
        name: state.emsGroups.hash[orgaGroupId].name
      })
    }

    return list
  }
}

/*
 * Getter to get Information for all EmsGroups in the tree structure
 *
 * @function
 *
 * @return {function}
 */
export const getEmsGroupsHierarchy = (state) => (filter) => createHierarchy(state, state.emsGroups.hierarchy, filter)

/*
 * Getter to get all EmsGroups for an emsId
 *
 * @function
 *
 * @return {function}
 */
export const getGroupsForEms = (state) => {
  return (emsId) => {
    if (state.emss[emsId]) {
      var returnGroups = {}
      for (var emsGroupId of state.emss[emsId]) {
        if (state.emsGroups.hash[emsGroupId]) {
          returnGroups[emsGroupId] = state.emsGroups.hash[emsGroupId]
        }
      }
      return returnGroups
    } else {
      return {}
    }
  }
}

/*
 * Helper function to read all emsGroup information into the tree structure
 *
 * @function
 *
 * recursively traverses the hierarchy tree and adds emsGroup information
 *
 * @param {store} state
 *
 * @param {object} emsGroups => mapping from emsGroupId => emsGroupIds
 *
 * @return {function}
 */
function createHierarchy(state, groups, filter) {
  const currentLayer = {}
  for (var groupId in groups) {
    const currentGroup = Object.assign({}, state.emsGroups.hash[groupId])
    currentGroup.children = createHierarchy(state, groups[groupId], filter)

    if (currentGroup.children.length || currentGroup.name.toUpperCase().includes(filter.toUpperCase()))
      currentLayer[groupId] = currentGroup
  }
  return sortSelectOptions(Object.values(currentLayer), { sortByProperty: 'name' })
}

/*
 * Helper function to read all emsGroup information from the tree structure into a flat object
 *
 * @function
 *
 * recursively traverses the hierarchy tree and flattens it
 *
 * @param {store} state
 *
 * @param {object} emsGroups
 *
 * @return {function}
 */
function gatherEmsGroups(state, emsGroups) {
  if (Object.keys(emsGroups).length) {
    var list = []
    for (var emsGroupId of Object.keys(emsGroups)) {
      list.push({
        id: state.emsGroups.hash[emsGroupId].id,
        name: state.emsGroups.hash[emsGroupId].name
      })
      if (Object.keys(emsGroups[emsGroupId]).length) {
        list = list.concat(gatherEmsGroups(state, emsGroups[emsGroupId]))
      }
    }
    return list
  } else {
    return []
  }
}

/*
 * Getter to get all EmsGroups as SelectOptions
 *
 * @function
 *
 * @return {function}
 */
export const groupsSelect = (state) => {
  const options = []

  for (const id in state.emsGroups.hash) {
    options.push({
      value: id,
      label: state.emsGroups.hash[id].name
    })
  }

  return options
}

/*
 * Getter to check if a Group has a certain tag
 *
 * @function
 *
 * @param {integer} groupId
 *
 * @param {string} tag 'TRAFO' | 'RESIDENCE'
 *
 * @return {function}
 */
export const hasTag = (state) => {
  return ({ groupId, tag }) => {
    return state.emsGroups.hash[groupId] ? state.emsGroups.hash[groupId].tag === tag : false
  }
}

export const getGroupIdForEms = (state) => {
  return (emsId) => {
    return state.emss?.[emsId]?.[0]
  }
}

/*
 * Getter to return all children GroupIds under a given root
 *
 * @function
 *
 * @param {integer} groupId
 *
 * @return {function}
 */
export const getGroupIdsFromRoot = (state) => {
  return (groupId) => {
    const groupIds = getTreeIds(state.emsGroups.hierarchy[groupId])
    groupIds.unshift(groupId)
    return groupIds
  }
}

const getTreeIds = (tree) => {
  let ids = []
  for (const id in tree) {
    ids.push(parseInt(id))
    ids = ids.concat(getTreeIds(tree[id]))
  }
  return ids
}
